import { cn } from "nazare-ui/utils";
import styles from "./styles/scroll-shadow.module.css";

/**
 * Rendered height of this app's shared sticky `<thead>` row — every
 * workflows table's `.th` uses the identical `padding: 10px 20px;
 * font-size: 11px;` shape (confirmed live: ~33.5px rendered), so one
 * constant covers all of them rather than each file guessing its own.
 * Pass as `<ScrollShadow topOffset={TABLE_HEADER_HEIGHT}>` for any
 * scrollable table with a sticky header; omit for a header-less scrollable
 * list (e.g. the dashboard summary cards).
 */
export const TABLE_HEADER_HEIGHT = 34;

export type ScrollShadowProps = {
  atTop: boolean;
  atBottom: boolean;
  /**
   * Pixels to push the TOP shadow down by — for a scrollable table with a
   * sticky `<thead>` row, this should be that row's rendered height, so the
   * shadow sits in the actual row-scrolling area right below the header
   * instead of overlapping the header itself. Without this, the shadow's
   * `top: 0` coincides with the header, and stacking it BEHIND the header
   * (z-index) makes it invisible rather than "under" in the intended,
   * vertically-below sense. Omit (default 0) for a scrollable list with no
   * header, e.g. the dashboard summary cards.
   */
  topOffset?: number;
};

/**
 * Renders the two curved edge-shadow overlays for a scrollable container —
 * pair with `useScrollEdges()` and `styles.scrollShadowWrapper` (see
 * `styles/scroll-shadow.module.css`'s doc comment for the required DOM
 * structure: this must be a sibling of the actual `overflow-y: auto`
 * element, both inside one `scrollShadowWrapper` div, NOT a descendant of
 * the scrolling element itself).
 */
export function ScrollShadow({ atTop, atBottom, topOffset = 0 }: ScrollShadowProps) {
  return (
    <>
      <div
        className={cn(styles.shadowTop, atTop && styles.shadowHidden)}
        style={topOffset ? { top: topOffset } : undefined}
        aria-hidden="true"
      />
      <div
        className={cn(styles.shadowBottom, atBottom && styles.shadowHidden)}
        aria-hidden="true"
      />
    </>
  );
}
